import { useId } from 'react'

/**
 * SeaSurface — top-down living-sea backdrop for the world-map canvas (the sea
 * "seen from above, like on a map"). Tropical depth gradient + drifting light
 * caustics, three layers of rippling swell lines, scattered foam whitecaps,
 * sun-glint sparkles and a couple of gulls gliding over. Same atmospheric
 * language as the Landing. Purely decorative (aria-hidden); drop it as the
 * first child of the (square, overflow-hidden) map canvas. Motion is gated on
 * `prefers-reduced-motion`.
 */
export function SeaSurface() {
  const uid = useId().replace(/:/g, '')

  const RIPPLE_A = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='28'%3E%3Cpath d='M0 18 Q40 6 80 18 T160 18' fill='none' stroke='%235FBDD0' stroke-width='2'/%3E%3C/svg%3E\")"
  const RIPPLE_B = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='44'%3E%3Cpath d='M0 28 Q60 10 120 28 T240 28' fill='none' stroke='%237FD0E0' stroke-width='2'/%3E%3C/svg%3E\")"
  const RIPPLE_C = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='110' height='20'%3E%3Cpath d='M0 13 Q27 5 55 13 T110 13' fill='none' stroke='%23BFE4ED' stroke-width='1.4'/%3E%3C/svg%3E\")"
  const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"

  const foam: [string, string, number, number][] = [
    ['22%', '30%', 0.3, 20], ['58%', '22%', 1.6, 24], ['74%', '48%', 0.9, 16],
    ['34%', '66%', 2.3, 20], ['80%', '78%', 1.1, 20], ['48%', '84%', 3, 16], ['12%', '52%', 1.9, 16],
  ]
  const sparks: [string, string, number, number][] = [
    ['18%', '16%', 0.2, 2], ['40%', '44%', 1.3, 3], ['70%', '34%', 2.1, 2], ['86%', '60%', 0.8, 2], ['56%', '70%', 1.7, 2],
  ]

  // faint maritime-chart filigree
  const grat = Array.from({ length: 9 }, (_, i) => (i + 1) * 100)
  const nodes: [number, number][] = [[300, 720], [760, 300]]
  const bearings = Array.from({ length: 16 }, (_, k) => (k * Math.PI) / 8)
  // compass rose reused 1:1 from the Landing (200-space, centred at 100,100)
  const roseTicks = Array.from({ length: 72 }, (_, i) => {
    const a = (i / 72) * Math.PI * 2
    const r1 = 86
    const r2 = i % 9 === 0 ? 76 : 82
    return { x1: 100 + Math.sin(a) * r1, y1: 100 - Math.cos(a) * r1, x2: 100 + Math.sin(a) * r2, y2: 100 - Math.cos(a) * r2 }
  })
  const frameTicks: number[] = []
  for (let v = 72; v <= 928; v += 46.4) frameTicks.push(v)

  return (
    <>
      <style>{`
        .ss-rip-${uid}{ position:absolute; inset:0; pointer-events:none; will-change:background-position; }
        .ss-ripA-${uid}{ background-image:${RIPPLE_A}; background-size:160px 28px; opacity:.13; animation:ss-rA-${uid} 20s linear infinite; }
        .ss-ripB-${uid}{ background-image:${RIPPLE_B}; background-size:240px 44px; opacity:.07; animation:ss-rB-${uid} 34s linear infinite reverse; }
        .ss-ripC-${uid}{ background-image:${RIPPLE_C}; background-size:110px 20px; opacity:.05; animation:ss-rC-${uid} 14s linear infinite; }
        @keyframes ss-rA-${uid}{ from{background-position:0 0} to{background-position:-160px 0} }
        @keyframes ss-rB-${uid}{ from{background-position:0 0} to{background-position:-240px 0} }
        @keyframes ss-rC-${uid}{ from{background-position:0 0} to{background-position:-110px 0} }
        .ss-caustic-${uid}{ position:absolute; border-radius:50%; pointer-events:none; will-change:transform,opacity; animation:ss-caustic-${uid} ease-in-out infinite; }
        @keyframes ss-caustic-${uid}{ 0%,100%{ transform:translate(0,0) scale(1); opacity:.45 } 50%{ transform:translate(22px,-16px) scale(1.1); opacity:.8 } }
        .ss-gull-${uid}{ position:absolute; left:0; will-change:left; animation:ss-fly-${uid} linear infinite; }
        .ss-gullbob-${uid}{ display:inline-block; animation:ss-bob-${uid} 4.5s ease-in-out infinite; }
        .ss-spark-${uid}{ position:absolute; border-radius:9999px; background:#F5EDD7; box-shadow:0 0 4px 1px rgba(245,237,215,.5); animation:ss-tw-${uid} 4s ease-in-out infinite; pointer-events:none; }
        .ss-foam-${uid}{ position:absolute; color:#E6F4F8; animation:ss-tw-${uid} 5s ease-in-out infinite; pointer-events:none; }
        @keyframes ss-fly-${uid}{ from{ left:-12% } to{ left:112% } }
        @keyframes ss-bob-${uid}{ 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-7px) } }
        @keyframes ss-tw-${uid}{ 0%,100%{ opacity:.15 } 50%{ opacity:.7 } }
        @media (prefers-reduced-motion: reduce){ .ss-rip-${uid},.ss-caustic-${uid},.ss-gull-${uid},.ss-gullbob-${uid},.ss-spark-${uid},.ss-foam-${uid}{ animation:none !important; } }
      `}</style>

      {/* tropical sea seen from above */}
      <div className="absolute inset-0" aria-hidden="true" style={{ background: 'radial-gradient(42% 36% at 24% 28%, rgba(63,182,201,.24), transparent 62%),radial-gradient(46% 40% at 78% 72%, rgba(63,182,201,.18), transparent 62%),radial-gradient(38% 34% at 64% 16%, rgba(95,189,208,.16), transparent 60%),radial-gradient(125% 120% at 50% 50%, #0c5e72 0%, #0a4252 52%, #07303a 100%)' }} />

      {/* caustics */}
      <div className={`ss-caustic-${uid}`} style={{ left: '14%', top: '18%', width: '34%', height: '26%', background: 'radial-gradient(circle, rgba(127,208,224,.20), transparent 65%)', animationDuration: '13s' }} aria-hidden="true" />
      <div className={`ss-caustic-${uid}`} style={{ left: '62%', top: '60%', width: '40%', height: '30%', background: 'radial-gradient(circle, rgba(127,208,224,.16), transparent 65%)', animationDuration: '17s', animationDelay: '-5s' }} aria-hidden="true" />
      <div className={`ss-caustic-${uid}`} style={{ left: '48%', top: '34%', width: '26%', height: '22%', background: 'radial-gradient(circle, rgba(229,204,143,.10), transparent 65%)', animationDuration: '21s', animationDelay: '-9s' }} aria-hidden="true" />

      {/* swell ripples */}
      <div className={`ss-rip-${uid} ss-ripB-${uid}`} aria-hidden="true" />
      <div className={`ss-rip-${uid} ss-ripA-${uid}`} aria-hidden="true" />
      <div className={`ss-rip-${uid} ss-ripC-${uid}`} aria-hidden="true" />

      {/* maritime-chart filigree (watermark) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <g stroke="#9fd3df" strokeWidth="1" opacity="0.06">
          {grat.map((v) => (
            <g key={v}><line x1={v} y1={0} x2={v} y2={1000} /><line x1={0} y1={v} x2={1000} y2={v} /></g>
          ))}
        </g>
        <g stroke="#E5CC8F" strokeWidth="0.8" opacity="0.05">
          {nodes.map(([cx, cy], ni) =>
            bearings.map((a, k) => {
              const dx = Math.cos(a), dy = Math.sin(a)
              return <line key={`${ni}-${k}`} x1={cx - dx * 1700} y1={cy - dy * 1700} x2={cx + dx * 1700} y2={cy + dy * 1700} />
            }),
          )}
        </g>
        <g transform="translate(500 520) scale(2.6) translate(-100 -100)" opacity="0.12">
          <g stroke="#E5CC8F" fill="none">
            <circle cx="100" cy="100" r="96" strokeWidth="0.5" /><circle cx="100" cy="100" r="86" strokeWidth="0.6" /><circle cx="100" cy="100" r="48" strokeWidth="0.4" />
          </g>
          <g stroke="#E5CC8F" strokeWidth="0.5">
            {roseTicks.map((t, i) => <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} />)}
          </g>
          {[45, 135, 225, 315].map((deg) => (
            <g key={deg} transform={`rotate(${deg} 100 100)`}>
              <path d="M100,50 L94,86 L100,100 Z" fill="#C9A55C" opacity="0.85" /><path d="M100,50 L106,86 L100,100 Z" fill="#7A3E1F" opacity="0.55" />
            </g>
          ))}
          {[0, 90, 180, 270].map((deg) => (
            <g key={deg} transform={`rotate(${deg} 100 100)`}>
              <path d="M100,12 L92,80 L100,100 Z" fill="#F5EDD7" opacity="0.95" /><path d="M100,12 L108,80 L100,100 Z" fill="#C9A55C" opacity="0.65" />
            </g>
          ))}
          <circle cx="100" cy="100" r="7" fill="#E5CC8F" /><circle cx="100" cy="100" r="3.4" fill="#0A3E4F" />
          <path d="M97,7 L97,1 L103,1 L103,7" stroke="#E5CC8F" strokeWidth="0.8" fill="none" />
        </g>
        <rect x="20" y="20" width="960" height="960" fill="none" stroke="#E5CC8F" strokeWidth="1.5" opacity="0.16" />
        <g stroke="#E5CC8F" strokeWidth="1.2" opacity="0.22">
          {frameTicks.map((v, i) => {
            const len = i % 2 === 0 ? 12 : 7
            return (
              <g key={v}>
                <line x1={v} y1={20} x2={v} y2={20 + len} /><line x1={v} y1={980} x2={v} y2={980 - len} />
                <line x1={20} y1={v} x2={20 + len} y2={v} /><line x1={980} y1={v} x2={980 - len} y2={v} />
              </g>
            )
          })}
        </g>
      </svg>

      {/* foam whitecaps */}
      {foam.map(([left, top, delay, w], i) => (
        <span key={`f${i}`} className={`ss-foam-${uid}`} style={{ left, top, animationDelay: `${delay}s` }} aria-hidden="true">
          <svg viewBox="0 0 24 8" width={w} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M1 5 Q5 1 9 5 T17 5" /></svg>
        </span>
      ))}

      {/* sparkles */}
      {sparks.map(([left, top, delay, s], i) => (
        <span key={`s${i}`} className={`ss-spark-${uid}`} style={{ left, top, width: s, height: s, animationDelay: `${delay}s` }} aria-hidden="true" />
      ))}

      {/* gulls */}
      <div className={`ss-gull-${uid}`} style={{ top: '13%', animationDuration: '28s', animationDelay: '-6s' }} aria-hidden="true">
        <span className={`ss-gullbob-${uid}`}>
          <svg viewBox="0 0 40 14" width={32} className="text-ocean-50/45" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2 10 C7 2 11 2 16 9 C21 2 25 2 30 10" /></svg>
        </span>
      </div>
      <div className={`ss-gull-${uid}`} style={{ top: '72%', animationDuration: '36s', animationDelay: '-20s' }} aria-hidden="true">
        <span className={`ss-gullbob-${uid}`} style={{ animationDelay: '.7s' }}>
          <svg viewBox="0 0 40 14" width={24} className="text-ocean-50/35" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2 10 C7 2 11 2 16 9 C21 2 25 2 30 10" /></svg>
        </span>
      </div>

      {/* vignette + grain */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ background: 'radial-gradient(125% 100% at 50% 45%, transparent 55%, rgba(4,18,24,.5) 100%)' }} />
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" aria-hidden="true" style={{ backgroundImage: GRAIN }} />
    </>
  )
}
